import {useMutation} from '@tanstack/react-query';
import React, {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {titleCase} from 'title-case';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventObjectName} from '../../../../../libs/shared/core/src/models/tracking/dataEventObjectName';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  getHasAdminSetupGuide,
  getHasRecipeBasedRules,
} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {AsyncActionButton} from '../../../../../libs/shared/ui/src/components/buttons/loadingButton/asyncActionButton';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {CenteredPopover} from '../../../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildTeamInboxPath} from '../../../../helpers/modelHelpers';
import {CircleModel} from '../../../../models/api/circleModel';
import {ApiAdminOnboardingResources} from '../../../../models/api/onboardingModels';
import {DepartmentsEnum} from '../../../../models/api/userMetadataModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {SettingsRuleResourcePagesEnum} from '../../../../models/url/models/rulesUrlModel';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
  SettingsSingleInboxSubPagesEnum,
} from '../../../../models/url/models/settingsInboxesUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsCirclesEditorTabsEnum} from '../../../../models/url/settingsUrlModel';
import {DelayParameter, delayToMilliseconds} from '../../../../settings/editors/commons/paramModel';
import {useInvalidateWorkflows} from '../../../../settings/fetchers/workflows';
import {useInboxOnCreate} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreator';
import {useSettingsInboxesNewCreatorState} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {getActivationPreferences, getCompany} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {
  TeamBusinessHoursPreferencesUpdatedAction,
  TeamPreferencesActionsEnum,
} from '../../../../store/data/teamPreferences/teamPreferencesActions';
import {pushPathAction} from '../../../../store/panels/app/appPanelRouteActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getAdminSetupTeamInbox} from '../../../../store/stateless/startGuideSelectors';
import {
  ContinueSetupTipsEnum,
  useOnboardingModalState,
} from '../../../app/topBar/onboarding/onboardingContext';
import {
  OnboardingAdminWizardVisual,
  OnboardingAdminWizardVisualFrame,
} from '../../welcome/onboardingAdminWizardVisual';
import {
  OnboardingInboxAccess,
  onboardingInboxAccessAll,
  OnboardingInboxSlide,
} from '../../welcome/onboardingInboxSlide';
import {OnboardingInboxVisualInboxPosition} from '../../welcome/onboardingInboxVisual';
import {OnboardingTeammateGroupsSlide} from '../../welcome/onboardingTeammateGroupsSlide';
import {generateStepText} from '../../welcome/onboardingWelcomeHelpers';
import {
  OnboardingWorkflowsAssignRuleAssignAlgorithm,
  OnboardingWorkflowsAssignRuleSlide,
} from '../../welcome/workflows/onboardingWorkflowsAssignRuleSlide';
import {
  OnboardingWorkflowsCreatedSlide,
  RWorkflowGoal,
  WorkflowGoal,
} from '../../welcome/workflows/onboardingWorkflowsCreatedSlide';
import {OnboardingWorkflowsOutcomeSlide} from '../../welcome/workflows/onboardingWorkflowsOutcomeSlide';
import {OnboardingWorkflowsOutcomesV2} from '../../welcome/workflows/onboardingWorkflowsOutcomesV2';
import {
  OnboardingWorkflowsProgressSlide,
  OnboardingWorkflowsProgressStages,
  Stage,
} from '../../welcome/workflows/onboardingWorkflowsProgressSlide';
import {OnboardingWorkflowsSlaRuleSlide} from '../../welcome/workflows/onboardingWorkflowsSlaRuleSlide';
import {
  OnboardingWorkflowsTagRuleCustomTagName,
  OnboardingWorkflowsTagRuleSlide,
  OnboardingWorkflowsTagRuleTagName,
  OnboardingWorkflowsTagRuleTagNamesConstants,
  parseCustomKeywords,
  parseCustomTagName,
} from '../../welcome/workflows/onboardingWorkflowsTagRuleSlide';
import {useSharedWorkspaceTaskIsComplete} from '../tasks/adminOnboardingTasks';

/*
 * Constants.
 */

enum AdminWizardSlidesEnum {
  INBOX = 'inbox_slide',
  TEAMMATE_GROUPS = 'teammate_group_slide',
  WORKFLOWS = 'workflows',
  WORKFLOWS_CREATE_SLA_RULE = 'workflows_create_sla_rule',
  WORKFLOWS_CREATE_TAG_RULE = 'workflows_create_tag_rule',
  WORKFLOWS_CREATE_ASSIGN_RULE = 'workflows_create_assign_rule',
  PROGRESS = 'progress',
  CREATED_RESOURCES = 'created_resources',
}

const SLIDE_TRACKING_OBJECT_NAME: Record<AdminWizardSlidesEnum, DataEventObjectName | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP,
  [AdminWizardSlidesEnum.INBOX]: OnboardingDataEventObject.ADMIN_WIZARD_INBOX,
  [AdminWizardSlidesEnum.WORKFLOWS]: OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE,
  [AdminWizardSlidesEnum.PROGRESS]: null,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES,
};

const VISUAL_FRAME: Record<AdminWizardSlidesEnum, OnboardingAdminWizardVisualFrame | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: OnboardingAdminWizardVisualFrame.TEAMMATE_GROUPS,
  [AdminWizardSlidesEnum.INBOX]: OnboardingAdminWizardVisualFrame.INBOX,
  [AdminWizardSlidesEnum.WORKFLOWS]: OnboardingAdminWizardVisualFrame.GOALS,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]: OnboardingAdminWizardVisualFrame.SLA_RULE,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]: OnboardingAdminWizardVisualFrame.TAG_RULE,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]: OnboardingAdminWizardVisualFrame.ASSIGN_RULE,
  [AdminWizardSlidesEnum.PROGRESS]: null,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingAdminWizardVisualFrame.CREATED_RESOURCES,
};

const VISUAL_POSITION: Record<AdminWizardSlidesEnum, OnboardingInboxVisualInboxPosition | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: null,
  [AdminWizardSlidesEnum.INBOX]: OnboardingInboxVisualInboxPosition.LEFT,
  [AdminWizardSlidesEnum.WORKFLOWS]: OnboardingInboxVisualInboxPosition.CENTER,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [AdminWizardSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [AdminWizardSlidesEnum.PROGRESS]: null,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingInboxVisualInboxPosition.CENTER,
};

/*
 * Props.
 */

export interface AdminWizardPopoverProps {
  panelId: AppPanelId;
  team: TeamModel;
  onChangeDepartment: (arg: DepartmentsEnum) => void;
  onClose: () => void;
  isAdminWizardOpen: boolean;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  border-radius: 16px;
  overflow: hidden;
`;

/*
 * Component.
 */

export const AdminWizardPopover: FC<AdminWizardPopoverProps> = (props) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(getUserId);
  const companyActivationPreferences = useAppSelector(getActivationPreferences);
  const {trackDataEvent} = useDataEventTracking();

  const {panelId, onChangeDepartment, onClose, team, isAdminWizardOpen} = props;
  const participatingInbox = useAppSelector(getAdminSetupTeamInbox);

  const supportsAdminWizardV3Rules = useAppSelector(getHasRecipeBasedRules);
  const presumedTotalStepCount = supportsAdminWizardV3Rules ? 4 : 2;

  const hasCompletedWizard = useSharedWorkspaceTaskIsComplete();
  const [slide, setSlide] = useState<AdminWizardSlidesEnum>(
    computeInitialSlideState(hasCompletedWizard ?? false),
  );
  const previousSlide = usePrevious(slide);

  const visualFrame = VISUAL_FRAME[slide];
  const previousVisualPosition = previousSlide && VISUAL_POSITION[previousSlide];
  const visualPosition = VISUAL_POSITION[slide];

  // If the user has already completed the wizard, show them the resources they created.
  const shouldShowViewCreatedResources =
    (previousSlide === null || previousSlide === AdminWizardSlidesEnum.CREATED_RESOURCES) &&
    slide === AdminWizardSlidesEnum.CREATED_RESOURCES;

  // Company name state is here because we want the modified company name to be reflected in the inbox slide animation.
  const company = useAppSelector(getCompany);

  // Teammate group slide state.
  const [selectedTeamType, setSelectedTeamType] = useState<DepartmentsEnum | string>('');
  const [createdCircle, setCreatedCircle] = useState<CircleModel | undefined>();
  const [otherTeam, setOtherTeam] = useState('');

  const [hasTeammateGroupError, setHasTeammateGroupError] = useState<boolean>(false);

  const {namespace} = team;

  const isOtherTeamType = selectedTeamType === DepartmentsEnum.OTHER;

  const teamDisplayName = useMemo(
    () => (isOtherTeamType ? otherTeam : titleCase(selectedTeamType.replace(/-/g, ' '))),
    [isOtherTeamType, otherTeam, selectedTeamType],
  );
  const [initialHasTeamType] = useState(Boolean(selectedTeamType));

  // Inbox slide state.
  const {inboxState, canSave, onSetColor, onSetName, onSetCircleIds, onSetIsPublic, onSetPinnedTeammatesIds} =
    useSettingsInboxesNewCreatorState(namespace);
  const inboxCreationHelper = useInboxOnCreate(namespace, inboxState);

  const {createdInbox} = inboxCreationHelper;

  const [workflowsOutcomes, setWorkflowsOutcomes] = useState<ReadonlyArray<OnboardingWorkflowsOutcomesV2>>(
    [],
  );
  const invalidateWorkflows = useInvalidateWorkflows();

  const [slaRuleSlaReplyTimeGoal, setSlaRuleSlaReplyTimeGoal] = useState<DelayParameter | undefined>(
    undefined,
  );

  const [tagRuleTagName, setTagRuleTagName] = useState<OnboardingWorkflowsTagRuleTagName | undefined>(
    undefined,
  );
  const [tagRuleCustomTagName, setTagRuleCustomTagName] = useState('');
  const [tagRuleSuggestedKeywords, setTagRuleSuggestedKeywords] = useState<ReadonlyArray<string>>([]);
  const [tagRuleCustomKeywords, setTagRuleCustomKeywords] = useState('');

  const [assignRuleAssignAlgorithm, setAssignRuleAssignAlgorithm] = useState<
    OnboardingWorkflowsAssignRuleAssignAlgorithm | undefined
  >(undefined);

  const visualInbox = createdInbox ?? participatingInbox ?? {color: 'grey', name: 'Shared inbox'};

  const resourceMutation = useMutation({
    mutationFn: async (variables: {outcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>}) => {
      if (!createdInbox || !createdCircle) {
        throw new Error('Missing inbox or circle.');
      }

      const payload = {
        workflow_goals: generateWorkflowGoals(
          variables.outcomes,
          slaRuleSlaReplyTimeGoal,
          tagRuleTagName,
          tagRuleCustomTagName,
          tagRuleSuggestedKeywords,
          tagRuleCustomKeywords,
          assignRuleAssignAlgorithm,
        ),
        inbox_id: createdInbox.id,
        circle_id: createdCircle.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        has_onboarding_prefilled_workspace_v3: true,
      };

      const createdResources = await postApiAsync(
        ApiAdminOnboardingResources,
        `${company.url}/namespaces/${namespace}/admin-onboarding/resources`,
        payload,
      );

      dispatch(dataUpdatedAction({company: createdResources.company}));

      if (createdResources.businessHours) {
        const action: TeamBusinessHoursPreferencesUpdatedAction = {
          type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED,
          payload: {
            teamId: team.id,
            businessHours: {...createdResources.businessHours, hasUpdateError: false},
          },
        };
        await dispatch(action);
      }

      invalidateWorkflows();
      return createdResources;
    },
  });

  const onCircleIdChange = (circleId: number) => {
    // Set team inbox default circle to the newly created circle upon creation.
    onInboxAccessChange(circleId);
  };

  const onInboxAccessChange = (access: OnboardingInboxAccess) => {
    if (access === onboardingInboxAccessAll) {
      onSetIsPublic(true);
      onSetCircleIds([]);
    } else {
      onSetIsPublic(false);
      onSetCircleIds([access]);
    }
  };

  const onNavigateToInbox = async () => {
    const inboxId = createdInbox?.id ?? participatingInbox?.id;
    if (inboxId !== undefined) {
      const teamInboxPath = buildTeamInboxPath(inboxId, InboxesFiltersEnum.UNASSIGNED);
      await dispatch(pushPathAction(panelId, teamInboxPath));
      invalidateWorkflows();
    }

    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_WIZARD_VIEW_INBOX);
    maybeOpenOnboardingChecklist();
    requestContinueSetupTip(ContinueSetupTipsEnum.COMPLETED_SHARED_WORKSPACE);
    onClose();
  };

  const onCancel = (event: DataEventObjectName) => {
    trackDataEvent(DataEventActionsEnum.CLICK, event, {});

    if (hasAdminSetupGuide) {
      openAdminSetupGuideSetupScreen();
    } else {
      maybeOpenOnboardingChecklist();
    }

    onClose();
  };

  const {maybeOpenOnboardingChecklist, requestContinueSetupTip, openAdminSetupGuideSetupScreen} =
    useOnboardingModalState();
  const hasAdminSetupGuide = useAppSelector(getHasAdminSetupGuide);

  useEffect(() => {
    const eventName = SLIDE_TRACKING_OBJECT_NAME[slide];
    if (!isAdminWizardOpen || !eventName) {
      return;
    }
    trackDataEvent(DataEventActionsEnum.VIEW, eventName);
  }, [slide, isAdminWizardOpen, trackDataEvent]);

  const navigateToPreviousWorkflowsSlide = () =>
    setSlide(getPreviousWorkflowsSlide(slide, workflowsOutcomes));
  const navigateToNextWorkflowsSlide = () => {
    const {nextSlide, isLastSlide} = getNextWorkflowsSlide(slide, workflowsOutcomes);

    setSlide(nextSlide);

    if (isLastSlide) {
      resourceMutation.mutate({outcomes: workflowsOutcomes});
    }
  };
  const getWorkflowsStepText = () => generateWorkflowsStepText(slide, workflowsOutcomes);

  const onNavigateToSharedInbox = (inboxNamespace: string, inboxId: number) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_SHARED_INBOX,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          namespace: inboxNamespace,
          inboxId,
          rootPage: SettingsRootPagesEnum.INBOXES,
          resourcePage: SettingsInboxesResourcePagesEnum.EDIT,
          subPage: SettingsSingleInboxSubPagesEnum.OVERVIEW,
          tabPage: SettingsInboxesTabPagesEnum.INBOXES,
        }),
      ),
    );
    onClose();
  };

  const onNavigateToRules = (rulesNamespace: string) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_RULES,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          namespace: rulesNamespace,
          rootPage: SettingsRootPagesEnum.RULES,
          resourcePage: SettingsRuleResourcePagesEnum.OVERVIEW,
          isInboxCreation: false,
          inboxId: undefined,
          ruleId: undefined,
          recipe: undefined,
          bundleId: undefined,
          filters: undefined,
        }),
      ),
    );
    onClose();
  };

  const onNavigateToCircle = (circleId: number) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_CIRCLE,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          circleId,
          rootPage: SettingsRootPagesEnum.CIRCLES,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          tabPage: SettingsCirclesEditorTabsEnum.MEMBERS,
        }),
      ),
    );
    onClose();
  };

  const renderSlide = (currentSlide: AdminWizardSlidesEnum) => {
    switch (currentSlide) {
      case AdminWizardSlidesEnum.TEAMMATE_GROUPS:
        return (
          <OnboardingTeammateGroupsSlide
            totalStepCount={presumedTotalStepCount}
            stepNumber={1}
            createdCircle={createdCircle}
            onCancel={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP_SKIP)}
            onNext={(circle: CircleModel | undefined) => {
              setCreatedCircle(circle);
              setSlide(AdminWizardSlidesEnum.INBOX);
              trackDataEvent(
                DataEventActionsEnum.CLICK,
                OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP_NEXT,
              );
              onSetPinnedTeammatesIds([userId]);
            }}
            selectedTeamType={selectedTeamType}
            setSelectedTeamType={setSelectedTeamType}
            setHasError={setHasTeammateGroupError}
            onChangeDepartment={onChangeDepartment}
            onCircleIdChange={onCircleIdChange}
            otherTeam={otherTeam}
            setOtherTeam={setOtherTeam}
            isOtherTeamType={isOtherTeamType}
            teamDisplayName={teamDisplayName}
            initialHasTeamType={initialHasTeamType}
          />
        );
      case AdminWizardSlidesEnum.INBOX:
        return (
          <OnboardingInboxSlide
            navSecondary={generateStepText(2, presumedTotalStepCount)}
            navPrimary={
              <ButtonGroup>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_INBOX_SKIP)}
                >
                  <Trans id="onboardinginbox-cancel">Set up on my own</Trans>
                </Button>

                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_PREVIOUS,
                    );
                    setSlide(AdminWizardSlidesEnum.TEAMMATE_GROUPS);
                  }}
                >
                  <Trans id="onboardinginbox-previous">Previous</Trans>
                </Button>

                <AsyncActionButton
                  variant={ButtonVariantsEnum.PRIMARY}
                  isDisabled={!canSave}
                  onClick={async () => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_NEXT,
                    );
                    await inboxCreationHelper.onCreateOrEdit();

                    if (!supportsAdminWizardV3Rules) {
                      setWorkflowsOutcomes([]);
                      resourceMutation.mutate({outcomes: []});
                      setSlide(AdminWizardSlidesEnum.PROGRESS);
                      return;
                    }

                    setSlide(AdminWizardSlidesEnum.WORKFLOWS);
                  }}
                >
                  <Trans id="onboardinginbox-next">Next</Trans>
                </AsyncActionButton>
              </ButtonGroup>
            }
            createdCircle={createdCircle}
            canSave={canSave}
            onSetColor={onSetColor}
            onSetName={onSetName}
            inboxState={inboxState}
            selectedTeamType={selectedTeamType}
          />
        );

      case AdminWizardSlidesEnum.WORKFLOWS:
        return (
          <OnboardingWorkflowsOutcomeSlide
            inboxState={inboxState}
            companyName={company.name}
            navPrimary={
              <ButtonGroup>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => {
                    setWorkflowsOutcomes([]);
                    resourceMutation.mutate({outcomes: []});
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_GOALS_SKIP,
                    );
                    setSlide(AdminWizardSlidesEnum.PROGRESS);
                  }}
                >
                  <Trans id="onboardingwelcome-workflows-slide-skip">Skip this step</Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_GOALS_PREVIOUS,
                    );
                    setSlide(AdminWizardSlidesEnum.INBOX);
                  }}
                >
                  <Trans id="onboardingwelcome-workflows-slide-previous">Previous</Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.PRIMARY}
                  isDisabled={workflowsOutcomes.length === 0}
                  onClick={() => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_GOALS_NEXT,
                      {inbox_goals: workflowsOutcomes},
                    );

                    if (workflowsOutcomes.length === 0) {
                      onNavigateToInbox();
                      return;
                    }

                    navigateToNextWorkflowsSlide();
                  }}
                >
                  <Trans id="onboardingwelcome-workflows-slide-next-v2">Next</Trans>
                </Button>
              </ButtonGroup>
            }
            navSecondary={generateStepText(3, presumedTotalStepCount)}
            selectedOutcomes={workflowsOutcomes}
            onChangeOutcomes={(outcomes) => setWorkflowsOutcomes(outcomes)}
          />
        );

      case AdminWizardSlidesEnum.WORKFLOWS_CREATE_SLA_RULE:
        return (
          <OnboardingWorkflowsSlaRuleSlide
            navSecondary={getWorkflowsStepText()}
            slaReplyTimeGoal={slaRuleSlaReplyTimeGoal}
            onChangeSlaReplyTimeGoal={setSlaRuleSlaReplyTimeGoal}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_SKIP,
                );
                setSlaRuleSlaReplyTimeGoal(undefined);
                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: !(slaRuleSlaReplyTimeGoal && delayToMilliseconds(slaRuleSlaReplyTimeGoal) > 0),
            })}
          />
        );

      case AdminWizardSlidesEnum.WORKFLOWS_CREATE_TAG_RULE:
        return (
          <OnboardingWorkflowsTagRuleSlide
            navSecondary={getWorkflowsStepText()}
            tagName={tagRuleTagName}
            onChangeTagName={setTagRuleTagName}
            customTagName={tagRuleCustomTagName}
            onChangeCustomTagName={setTagRuleCustomTagName}
            suggestedKeywords={tagRuleSuggestedKeywords}
            onChangeSuggestedKeywords={setTagRuleSuggestedKeywords}
            customKeywords={tagRuleCustomKeywords}
            onChangeCustomKeywords={setTagRuleCustomKeywords}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_SKIP,
                );

                setTagRuleTagName(undefined);
                setTagRuleCustomTagName('');
                setTagRuleSuggestedKeywords([]);
                setTagRuleCustomKeywords('');

                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: !tagGoalIsComplete(
                tagRuleTagName,
                tagRuleCustomTagName,
                tagRuleSuggestedKeywords,
                tagRuleCustomKeywords,
              ),
            })}
          />
        );

      case AdminWizardSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE:
        return (
          <OnboardingWorkflowsAssignRuleSlide
            inboxState={inboxState}
            createdCircle={createdCircle}
            navSecondary={getWorkflowsStepText()}
            assignAlgorithm={assignRuleAssignAlgorithm}
            onChangeAssignAlgorithm={setAssignRuleAssignAlgorithm}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_SKIP,
                );
                setAssignRuleAssignAlgorithm(undefined);
                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: assignRuleAssignAlgorithm === undefined,
            })}
          />
        );

      case AdminWizardSlidesEnum.PROGRESS:
        return (
          <OnboardingWorkflowsProgressSlide
            stages={getProgressStages(
              Object.keys(
                generateWorkflowGoals(
                  workflowsOutcomes,
                  slaRuleSlaReplyTimeGoal,
                  tagRuleTagName,
                  tagRuleCustomTagName,
                  tagRuleSuggestedKeywords,
                  tagRuleCustomKeywords,
                  assignRuleAssignAlgorithm,
                ),
              ).filter(RWorkflowGoal.is),
            )}
            isLoading={resourceMutation.isLoading}
            onFinished={() => setSlide(AdminWizardSlidesEnum.CREATED_RESOURCES)}
          />
        );

      case AdminWizardSlidesEnum.CREATED_RESOURCES:
        return (
          <OnboardingWorkflowsCreatedSlide
            navPrimary={
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => onNavigateToInbox()}>
                <Trans id="onboardingwelcome-workflows-created-slide-view-shared-inbox">
                  View shared inbox
                </Trans>
              </Button>
            }
            adminOnboardingResources={companyActivationPreferences.adminOnboardingResources}
            shouldShowViewCreatedResources={shouldShowViewCreatedResources}
            companyName={company.name}
            team={team}
            onNavigateToRules={onNavigateToRules}
            onNavigateToSharedInbox={onNavigateToSharedInbox}
            onNavigateToCircle={onNavigateToCircle}
            inbox={visualInbox}
            tags={resourceMutation.data?.tags ?? []}
            rules={resourceMutation.data?.rules ?? []}
          />
        );

      default:
        throw new UnreachableError(currentSlide);
    }
  };

  if (!isAdminWizardOpen) {
    return null;
  }

  const hasAnyError = hasTeammateGroupError || inboxCreationHelper.hasError || resourceMutation.isError;
  if (hasAnyError) {
    return (
      <OkCancelDialog
        title={<Trans id="onboardingwelcome-error-dialog-title">Some items didn&apos;t save</Trans>}
        onOkClick={onClose}
        isCancelHidden
      >
        <Trans id="onboardingwelcome-error-dialog-tryagain">
          There were unsaved items due to an unexpected error. You&apos;ll get a chance to try again later
          from the setup guide.
        </Trans>
      </OkCancelDialog>
    );
  }

  return (
    <CenteredPopover>
      <StyledContainerDiv>
        {renderSlide(slide)}
        {visualFrame !== null && (
          <OnboardingAdminWizardVisual
            frame={visualFrame}
            selectedTeamType={selectedTeamType}
            teamDisplayName={teamDisplayName}
            initialHasTeamType={initialHasTeamType}
            inboxState={inboxState}
            companyName={company.name}
            createdCircle={createdCircle}
            position={visualPosition ?? undefined}
            previousPosition={previousVisualPosition ?? undefined}
            team={team}
            slaRuleSlaReplyTimeGoal={slaRuleSlaReplyTimeGoal}
            tagRuleTagName={
              tagRuleTagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
                ? parseCustomTagName(tagRuleCustomTagName)
                : (tagRuleTagName &&
                    OnboardingWorkflowsTagRuleTagNamesConstants[tagRuleTagName].displayName) ??
                  ''
            }
            tagRuleKeywords={[
              ...new Set([...tagRuleSuggestedKeywords, ...parseCustomKeywords(tagRuleCustomKeywords)]),
            ]}
          />
        )}
      </StyledContainerDiv>
    </CenteredPopover>
  );
};

function mapWorkflowsOutcomeToSlide(workflowsOutcome: OnboardingWorkflowsOutcomesV2): AdminWizardSlidesEnum {
  switch (workflowsOutcome) {
    case OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING:
      return AdminWizardSlidesEnum.WORKFLOWS_CREATE_SLA_RULE;
    case OnboardingWorkflowsOutcomesV2.HANDLED:
      return AdminWizardSlidesEnum.WORKFLOWS_CREATE_TAG_RULE;
    case OnboardingWorkflowsOutcomesV2.CATEGORIZED:
      return AdminWizardSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE;
    default:
      throw new UnreachableError(workflowsOutcome);
  }
}

function getWorkflowsSlides(workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>) {
  const workflowsOutcomesOrder = [
    OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING,
    OnboardingWorkflowsOutcomesV2.HANDLED,
    OnboardingWorkflowsOutcomesV2.CATEGORIZED,
  ];

  const sortedWorkflowsOutcomes = [...workflowsOutcomes];
  sortedWorkflowsOutcomes.sort(
    (a, b) => workflowsOutcomesOrder.indexOf(a) - workflowsOutcomesOrder.indexOf(b),
  );

  return sortedWorkflowsOutcomes.map((outcome) => mapWorkflowsOutcomeToSlide(outcome));
}

function getPreviousWorkflowsSlide(
  slide: AdminWizardSlidesEnum,
  workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
) {
  const workflowSlides = getWorkflowsSlides(workflowsOutcomes);
  const slideIndex = workflowSlides.indexOf(slide);
  const nextSlide = slideIndex === -1 ? workflowSlides[0] : workflowSlides[slideIndex - 1];

  if (!nextSlide) {
    return AdminWizardSlidesEnum.WORKFLOWS;
  }

  return nextSlide;
}

function getNextWorkflowsSlide(
  slide: AdminWizardSlidesEnum,
  workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
) {
  const workflowSlides = getWorkflowsSlides(workflowsOutcomes);
  const slideIndex = workflowSlides.indexOf(slide);
  const nextSlide = slideIndex === -1 ? workflowSlides[0] : workflowSlides[slideIndex + 1];

  if (!nextSlide) {
    return {nextSlide: AdminWizardSlidesEnum.PROGRESS, isLastSlide: true};
  }

  return {nextSlide, isLastSlide: false};
}

function generateWorkflowsStepText(
  slide: AdminWizardSlidesEnum,
  workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
) {
  const offset = 3; // We start from the third slide - AdminWizardSlidesEnum.WORKFLOWS
  const workflowSlides = getWorkflowsSlides(workflowsOutcomes);
  const slideIndex = workflowSlides.indexOf(slide);

  return generateStepText(offset + slideIndex + 1, offset + workflowSlides.length);
}

function tagGoalIsComplete(
  tagRuleTagName: OnboardingWorkflowsTagRuleTagName | undefined,
  tagRuleCustomTagName: string,
  tagRuleSuggestedKeywords: ReadonlyArray<string>,
  tagRuleCustomKeywords: string,
) {
  return Boolean(
    (tagRuleTagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
      ? parseCustomTagName(tagRuleCustomTagName)
      : tagRuleTagName) &&
      (tagRuleSuggestedKeywords.length || parseCustomKeywords(tagRuleCustomKeywords).length),
  );
}

function generateWorkflowGoals(
  workflowOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
  slaReplyTime: DelayParameter | undefined,
  tagName: OnboardingWorkflowsTagRuleTagName | undefined,
  customTagName: string,
  suggestedKeywords: ReadonlyArray<string>,
  customKeywords: string,
  assignAlgorithm: OnboardingWorkflowsAssignRuleAssignAlgorithm | undefined,
) {
  const slaGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING) &&
    slaReplyTime &&
    delayToMilliseconds(slaReplyTime) > 0
      ? {
          [WorkflowGoal.ENSURE_TIMELY_RESPONSE_TO_CUSTOMERS]: {
            sla_reply_time_goal_ms: delayToMilliseconds(slaReplyTime),
          },
        }
      : {};

  const tagGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.HANDLED) &&
    tagName !== undefined &&
    tagGoalIsComplete(tagName, customTagName, suggestedKeywords, customKeywords)
      ? {
          [WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS]: {
            tag_name:
              tagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
                ? parseCustomTagName(customTagName)
                : OnboardingWorkflowsTagRuleTagNamesConstants[tagName].displayName,
            keywords: [...new Set([...suggestedKeywords, ...parseCustomKeywords(customKeywords)])],
          },
        }
      : {};

  const assignGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.CATEGORIZED) && assignAlgorithm
      ? {
          [WorkflowGoal.GIVE_EACH_CONVERSATION_A_CLEAR_OWNER]: {
            assign_algorithm: assignAlgorithm,
          },
        }
      : {};

  return {
    ...slaGoal,
    ...tagGoal,
    ...assignGoal,
  };
}

function renderWorkflowsCreateRuleNavigation(props: {
  onClickSkip: () => void;
  onNavigateToPreviousWorkflowsSlide: () => void;
  onNavigateToNextWorkflowsSlide: () => void;
  isNextDisabled: boolean;
}) {
  const {onClickSkip, onNavigateToPreviousWorkflowsSlide, onNavigateToNextWorkflowsSlide, isNextDisabled} =
    props;

  return (
    <ButtonGroup>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onClickSkip}>
        <Trans id="onboardinginbox-skip">Skip this step</Trans>
      </Button>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onNavigateToPreviousWorkflowsSlide}>
        <Trans id="onboardinginbox-previous">Previous</Trans>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={isNextDisabled}
        onClick={onNavigateToNextWorkflowsSlide}
      >
        <Trans id="onboardinginbox-next">Next</Trans>
      </Button>
    </ButtonGroup>
  );
}

function computeInitialSlideState(hasCompletedWizard: boolean) {
  // Shortcut to make testing easier, so we can retry the flow without creating a new company.
  if (window.location.hash === '#retry-admin-wizard') {
    return AdminWizardSlidesEnum.TEAMMATE_GROUPS;
  }

  // Start at the end, so users can see the resources they created.
  if (hasCompletedWizard) {
    return AdminWizardSlidesEnum.CREATED_RESOURCES;
  }

  // Start from the begining
  return AdminWizardSlidesEnum.TEAMMATE_GROUPS;
}

function getProgressStages(workflowGoals: ReadonlyArray<WorkflowGoal>): OnboardingWorkflowsProgressStages {
  if (workflowGoals.includes(WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS)) {
    return [Stage.INBOX, Stage.TAGS, Stage.RULES];
  }

  if (workflowGoals.length) {
    return [Stage.INBOX, Stage.RULES];
  }

  return [Stage.INBOX];
}
